'use client';

import { Box, Button, Chip, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, useId } from 'react';
import { RequestCardMenu, type RequestCardMenuItem } from './RequestCardMenu';
import {
  resolveTypedRequestCardCopy,
  type TypedRequestCardCopy,
  type TypedRequestCardCopyOverrides,
} from './typedRequestCardCopy';

export type { TypedRequestCardCopy, TypedRequestCardCopyOverrides };
export type TypedRequestCardMenuItem = RequestCardMenuItem;

export type TypedRequestCardProps = {
  /** Who is asking. */
  providerName: string;
  /** The provider's parent organisation. Nothing is drawn when it is absent. */
  parentName?: string;
  /** A typed request names an entity type. A Legacy request asks for any identity. */
  kind: 'typed' | 'legacy';
  /** What a typed request asks for, such as an entity type's name. Not used on a Legacy card. */
  asksLabel?: string;
  /** The customer has a draft for this request. Only a typed card shows it. */
  draftInProgress: boolean;
  /** The primary: Start or Continue on a typed card, Grant now on a Legacy card. */
  onPrimary: () => void;
  /**
   * The items of the menu button, in order. Dismiss is the first and, for now, the only one. With
   * no items the card draws no menu button.
   */
  menuItems: TypedRequestCardMenuItem[];
  /** Replaces any of the default strings. Strings left out keep their default. */
  copy?: TypedRequestCardCopyOverrides;
  'data-testid'?: string;
};

type TextProps = Pick<
  TypedRequestCardProps,
  'providerName' | 'parentName' | 'kind' | 'asksLabel'
> & {
  /** The id of the whole block of text: the buttons are described by it. */
  id: string;
  /** The id of the provider's name: it names the card. */
  titleId: string;
  showsDraft: boolean;
  copy: TypedRequestCardCopy;
};

// A Legacy request asks for any identity and names no type. A typed request names its type, and
// draws nothing when the parent has no type to name.
function AsksLine({
  kind,
  asksLabel,
  asksFor,
}: Readonly<Pick<TextProps, 'kind' | 'asksLabel'> & { asksFor: string }>): ReactElement | null {
  if (kind === 'legacy' || !asksLabel) {
    return null;
  }
  return (
    <Typography variant="body1" component="p">
      {asksFor}{' '}
      <Box
        component="strong"
        sx={(theme: Theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
      >
        {asksLabel}
      </Box>
    </Typography>
  );
}

function DraftTag({ copy }: Readonly<{ copy: TypedRequestCardCopy }>): ReactElement {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.semantic.spacing.xs,
        marginTop: theme.semantic.spacing['2xs'],
      })}
    >
      <Chip
        label={copy.draft}
        size="small"
        variant="outlined"
        sx={(theme: Theme) => ({ fontSize: theme.semantic.fontSize.xs })}
      />
      <Typography
        variant="body2"
        component="span"
        sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}
      >
        {copy.draftInProgress}
      </Typography>
    </Box>
  );
}

function RequestText({
  id,
  titleId,
  providerName,
  parentName,
  kind,
  asksLabel,
  showsDraft,
  copy,
}: Readonly<TextProps>): ReactElement {
  return (
    <Box
      id={id}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minWidth: 0,
        gap: theme.semantic.spacing['2xs'],
      })}
    >
      <Typography
        id={titleId}
        variant="h6"
        component="p"
        sx={(theme: Theme) => ({
          fontSize: theme.semantic.fontSize.lg,
          overflowWrap: 'anywhere',
        })}
      >
        {providerName}
      </Typography>
      {parentName && (
        <Typography
          variant="body2"
          component="p"
          sx={(theme: Theme) => ({
            color: theme.palette.text.tertiary,
            overflowWrap: 'anywhere',
          })}
        >
          {parentName}
        </Typography>
      )}
      <AsksLine kind={kind} asksLabel={asksLabel} asksFor={copy.asksFor} />
      {showsDraft && <DraftTag copy={copy} />}
    </Box>
  );
}

// The board draws the buttons 44px high, and a text button with 8px each side.
const primarySx = (theme: Theme) => ({ minHeight: theme.requestActions.buttonHeight });

const legacySx = (theme: Theme) => ({
  ...primarySx(theme),
  color: theme.palette.text.primary,
  paddingInline: theme.semantic.spacing.xs,
});

type PrimaryProps = {
  isLegacy: boolean;
  showsDraft: boolean;
  copy: TypedRequestCardCopy;
  describedBy: string;
  onPrimary: () => void;
};

// A Legacy card has a text button, Grant now. A typed card has a filled button: Start, or Continue
// while a draft is in progress.
function PrimaryButton({
  isLegacy,
  showsDraft,
  copy,
  describedBy,
  onPrimary,
}: Readonly<PrimaryProps>): ReactElement {
  const typedLabel = showsDraft ? copy.continue : copy.start;
  return (
    <Button
      variant={isLegacy ? 'text' : 'contained'}
      size="large"
      sx={isLegacy ? legacySx : primarySx}
      data-action="request-card-primary"
      aria-describedby={describedBy}
      onClick={() => onPrimary()}
    >
      {isLegacy ? copy.grantNow : typedLabel}
    </Button>
  );
}

/**
 * One open request on the Requests tab. It shows who is asking and, for a typed request, what for.
 * The primary is Start, or Continue while a draft is in progress (with the Draft chip). A Legacy
 * request asks for any identity: it has no asks line and its action is a text button, Grant now.
 * The round button at the top right opens a menu whose items the parent gives (for now, only
 * Dismiss).
 *
 * Presentational: the card holds no request. The parent passes everything and acts on each
 * callback. The one thing the card holds is whether its menu is open.
 */
function TypedRequestCardComponent({
  providerName,
  parentName,
  kind,
  asksLabel,
  draftInProgress,
  onPrimary,
  menuItems,
  copy: copyOverrides,
  'data-testid': dataTestId,
}: Readonly<TypedRequestCardProps>): ReactElement {
  const copy = resolveTypedRequestCardCopy(copyOverrides);
  const textId = useId();
  const titleId = useId();
  const isLegacy = kind === 'legacy';
  const showsDraft = !isLegacy && draftInProgress;
  // With no items the menu would open onto nothing, so the button is not drawn.
  const hasMenu = menuItems.length > 0;

  return (
    <Box
      component="article"
      aria-labelledby={titleId}
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        gap: theme.semantic.spacing.xs,
        paddingBlock: theme.semantic.spacing.sm,
        paddingInline: theme.semantic.spacing.base,
        borderRadius: theme.semantic.borderRadius.sm,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: theme.semantic.spacing.xs,
        })}
      >
        <RequestText
          id={textId}
          titleId={titleId}
          providerName={providerName}
          parentName={parentName}
          kind={kind}
          asksLabel={asksLabel}
          showsDraft={showsDraft}
          copy={copy}
        />
        {hasMenu && <RequestCardMenu label={copy.menuLabel(providerName)} items={menuItems} />}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryButton
          isLegacy={isLegacy}
          showsDraft={showsDraft}
          copy={copy}
          describedBy={textId}
          onPrimary={onPrimary}
        />
      </Box>
    </Box>
  );
}

export const TypedRequestCard = withClarityTheme(TypedRequestCardComponent);
